import { useEffect } from 'react'
import type { CSSProperties } from 'react'
import { getMotif } from './motifs'
import WaxSeal from './WaxSeal'
import PaintedCard from './PaintedCard'
import type { PaintedWords } from './PaintedCard'
import { loadTemplateFonts, sampleEventFor, sampleTitleFor } from '../../lib/inviteTemplates'
import type { InviteTemplate } from '../../lib/inviteTemplates'
import { getInviteFont } from '../../lib/inviteStyle'
import { resolvePaintedChoice } from '../../lib/paintedInvite'
import './invite-thumb.css'

/**
 * A static miniature of a template: its paper and glows, two corners of its
 * motif, a sample title in its own display face, a date line and the RSVP
 * pill. Cheap DOM — no iframe — so the picker and the gallery can show a
 * hundred of them at once. Only the one the host selects is rendered for real
 * (InviteTemplatePicker's single preview iframe).
 *
 * A painted template's thumbnail is its PaintedCard in thumb mode, carrying
 * the host's own words and colours when the caller has them.
 *
 * `bare` is the design's picture alone, for a backdrop (the dashboard's event
 * cards): the same cheap thumbnail, without the words.
 */

type Props = {
  template: InviteTemplate
  lang?: string
  className?: string
  words?: PaintedWords | null
  accent?: string | null
  background?: string | null
  bare?: boolean
}

/** A calendar day a couple of months out, so sample cards never show a past date. */
const sampleDate = () => {
  const d = new Date()
  d.setMonth(d.getMonth() + 2, 12)
  return d.toISOString().slice(0, 10)
}

export const sampleWordsFor = (template: InviteTemplate, lang: string): PaintedWords => {
  const sample = sampleEventFor(template.category, lang)
  return { title: sample.title, honoree: sample.honoree_name, date: sampleDate(), time: '16:00', location: sample.location }
}

const TemplateThumb = ({ template, lang = 'en', className = '', words, accent, background, bare = false }: Props) => {
  if (template.painted) {
    return (
      <PaintedCard
        def={template.painted}
        choice={resolvePaintedChoice(template.painted, accent, background)}
        words={bare ? { title: '' } : words?.title ? words : sampleWordsFor(template, lang)}
        lang={lang}
        mode="thumb"
        bare={bare}
        className={`tpl-thumb-painted ${className}`}
      />
    )
  }
  return <ClassicThumb template={template} lang={lang} className={className} bare={bare} />
}

const ClassicThumb = ({ template, lang = 'en', className = '', bare = false }: Props) => {
  const p = template.palette
  const font = getInviteFont(template.defaults.font)
  const { Icon } = getMotif(template.motif)
  const t = template.title

  useEffect(() => {
    loadTemplateFonts(template.id)
  }, [template.id])

  const vars = {
    '--inv-paper': p.paper,
    '--inv-ink': p.ink,
    '--inv-a1': p.a1,
    '--inv-a2': p.a2,
    '--inv-gold': p.gold,
    '--tt-title': p.title,
    '--tt-display': font?.display || 'inherit',
    '--tt-body': font?.body || 'inherit',
    '--tt-transform': t.transform || 'none',
    '--tt-weight': String(t.weight || 500),
    '--tt-style': t.italic === false ? 'normal' : t.italic === true ? 'italic' : template.legacy ? 'normal' : 'italic',
    '--tt-tracking': t.tracking || 'normal',
  } as CSSProperties

  const showMotif = template.motif !== 'none'

  return (
    <div className={`tpl-thumb${template.dark ? ' tpl-thumb-dark' : ''} ${className}`} style={vars} aria-hidden="true">
      {showMotif && (
        <svg className="tpl-thumb-mo tl" viewBox="0 0 100 100">
          <Icon />
        </svg>
      )}
      {showMotif && (
        <svg className="tpl-thumb-mo br" viewBox="0 0 100 100">
          <Icon />
        </svg>
      )}
      {!bare && (
      <div className="tpl-thumb-body">
        <p className="tpl-thumb-kicker">{lang === 'ka' ? 'მოწვეული ხართ' : "You're invited"}</p>
        <p className="tpl-thumb-title">{sampleTitleFor(template.category, lang)}</p>
        <p className="tpl-thumb-date">{lang === 'ka' ? '12 სექტემბერი' : '12 September'}</p>
        <span className="tpl-thumb-pill">RSVP</span>
      </div>
      )}
      {/* The template's own wax seal, tucked into the corner as on the
          invitation's photo frame. The originals keep their photographs and
          show none here. */}
      {!template.legacy && (
        <span className="tpl-thumb-seal">
          <WaxSeal motif={template.motif} color={p.a1} size="100%" shadow={false} />
        </span>
      )}
    </div>
  )
}

export default TemplateThumb
